import React, { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { CalendarDays, ClipboardCheck, MapPin, Users } from 'lucide-react';
import type { CoachSessionSelfAttendanceStatus } from '@court-sync/shared';
import type { LessonEnrollmentRow } from '@/services/lesson';
import { lessonService } from '@/services/lesson';
import type { LessonOccurrenceClickPayload } from '@/utils/scheduleOccurrences';
import { courtNamesLabelFromLesson } from '@/utils/courtDisplay';
import { formatTimeHHMM } from '@/utils/timeFormat';
import { Modal } from '@/components/ui/Modal';
import { AttendanceToggle } from '@/components/lessons/AttendanceToggle';
import { CoachSelfCheckInActions } from '@/components/coach/CoachSelfCheckInActions';
import toast from 'react-hot-toast';
import { countMarkedStudentAttendance } from '@court-sync/shared';
import { coachService, type CoachScheduleData } from '@/services/coach';
import { useCoachAcademyStore } from '@/store/coachAcademy';
import { getOccurrenceScopeLabel } from '@/utils/bookingScopeLabel';

function rosterRowKey(row: LessonEnrollmentRow): string {
  return row.bookingId ?? row.makeupSessionReservationId ?? row.studentId;
}

export interface CoachLessonOccurrenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  occurrence: LessonOccurrenceClickPayload | null;
  academyId: string;
  schedule: CoachScheduleData | null;
  courtNamesById?: Record<string, string>;
  onAttendanceSaved: () => Promise<void>;
}

export function CoachLessonOccurrenceModal({
  isOpen,
  onClose,
  occurrence,
  academyId,
  schedule,
  courtNamesById,
  onAttendanceSaved,
}: CoachLessonOccurrenceModalProps) {
  const academy = useCoachAcademyStore((s) => s.academy);
  const allowSelfCheckIn = academy?.allowCoachSelfAttendance === true;

  const [enrollments, setEnrollments] = useState<LessonEnrollmentRow[]>([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);
  const [markingBookingId, setMarkingBookingId] = useState<string | null>(null);
  const [selfAttendance, setSelfAttendance] = useState<CoachSessionSelfAttendanceStatus | null>(
    null
  );
  const [loadingSelfAttendance, setLoadingSelfAttendance] = useState(false);
  const [checkingInSelf, setCheckingInSelf] = useState(false);

  const loadEnrollments = useCallback(async () => {
    if (!occurrence || !academyId) return;
    setLoadingEnrollments(true);
    try {
      const res = await lessonService.getLessonEnrollments(occurrence.lesson.id, {
        academyId,
        sessionDate: occurrence.sessionDateYmd,
      });
      if (res.success && res.data) setEnrollments(res.data);
      else {
        setEnrollments([]);
        toast.error((res as { error?: string }).error ?? 'Failed to load roster');
      }
    } catch (e: unknown) {
      setEnrollments([]);
      toast.error(e instanceof Error ? e.message : 'Failed to load roster');
    } finally {
      setLoadingEnrollments(false);
    }
  }, [occurrence, academyId]);

  const loadSelfAttendance = useCallback(async () => {
    if (!occurrence || !academyId || !allowSelfCheckIn) {
      setSelfAttendance(null);
      return;
    }
    setLoadingSelfAttendance(true);
    try {
      const status = await coachService.getMySessionAttendanceStatus({
        academyId,
        lessonId: occurrence.lesson.id,
        sessionDate: occurrence.sessionDateYmd,
        sessionId: occurrence.sessionId,
      });
      setSelfAttendance(status);
    } catch {
      setSelfAttendance(null);
    } finally {
      setLoadingSelfAttendance(false);
    }
  }, [occurrence, academyId, allowSelfCheckIn]);

  useEffect(() => {
    if (isOpen && occurrence) {
      loadEnrollments();
      void loadSelfAttendance();
    }
  }, [isOpen, occurrence, loadEnrollments, loadSelfAttendance]);

  const handleSelfCheckIn = useCallback(
    async (status: 'attended' | 'scheduled') => {
      if (!occurrence || !academyId) return;
      setCheckingInSelf(true);
      try {
        await coachService.checkInSelfSession({
          academyId,
          lessonId: occurrence.lesson.id,
          sessionDate: occurrence.sessionDateYmd,
          sessionId: occurrence.sessionId,
          status,
        });
        toast.success(status === 'attended' ? 'Checked in' : 'Check-in cleared');
        await loadSelfAttendance();
      } catch (e: unknown) {
        toast.error(e instanceof Error ? e.message : 'Failed to update check-in');
      } finally {
        setCheckingInSelf(false);
      }
    },
    [occurrence, academyId, loadSelfAttendance]
  );

  const handleMarkAttendance = useCallback(
    async (
      row: LessonEnrollmentRow,
      status: 'scheduled' | 'attended' | 'missed' | 'cancelled'
    ) => {
      if (!occurrence || !row.bookingId) return;
      const bookingId = row.bookingId;
      const previousStatus = row.attendanceStatus ?? 'scheduled';

      setMarkingBookingId(bookingId);
      setEnrollments((prev) =>
        prev.map((r) => (r.bookingId === bookingId ? { ...r, attendanceStatus: status } : r))
      );

      try {
        await coachService.markAttendance(
          academyId,
          occurrence.lesson.id,
          occurrence.sessionDateYmd,
          [{ bookingId, studentId: row.studentId, status }],
          occurrence.sessionId ? { sessionId: occurrence.sessionId } : undefined
        );
        toast.success(status === 'scheduled' ? 'Attendance cleared' : 'Attendance saved');
        await onAttendanceSaved();
      } catch (e: unknown) {
        setEnrollments((prev) =>
          prev.map((r) =>
            r.bookingId === bookingId ? { ...r, attendanceStatus: previousStatus } : r
          )
        );
        toast.error(e instanceof Error ? e.message : 'Failed to save attendance');
        await loadEnrollments();
      } finally {
        setMarkingBookingId(null);
      }
    },
    [occurrence, academyId, onAttendanceSaved, loadEnrollments]
  );

  if (!occurrence) return null;

  const lesson = occurrence.lesson;
  const sessionScheduled = occurrence.status === 'scheduled';
  const termName = lesson.termId ? schedule?.terms[lesson.termId]?.name : undefined;
  const courtLabel = courtNamesLabelFromLesson(lesson, courtNamesById);
  const dateLabel = format(occurrence.date, 'EEEE, MMM d, yyyy');

  const rosterRows = enrollments.filter(
    (r) => r.status === 'confirmed' || r.status === 'pending'
  );
  const markedStudentCount = countMarkedStudentAttendance(
    rosterRows.map((r) => r.attendanceStatus ?? 'scheduled')
  );

  const statusBadge =
    occurrence.status === 'scheduled'
      ? 'bg-green-100 dark:bg-green-900/40 text-green-900 dark:text-green-100'
      : occurrence.status === 'cancelled'
        ? 'bg-red-100 dark:bg-red-900/40 text-red-900 dark:text-red-100'
        : 'bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100';

  const statusLabel =
    occurrence.status === 'scheduled'
      ? 'Scheduled'
      : occurrence.status === 'cancelled'
        ? 'Cancelled'
        : 'Postponed';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={occurrence.lessonName} maxWidth="2xl">
      <div className="space-y-4 text-sm text-gray-800 dark:text-gray-200">
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700">
            <CalendarDays className="h-3 w-3 shrink-0" aria-hidden />
            {dateLabel}
          </span>
          <span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700">
            {formatTimeHHMM(occurrence.startTime)} – {formatTimeHHMM(occurrence.endTime)}
          </span>
          <span className={`px-2 py-0.5 rounded ${statusBadge}`}>{statusLabel}</span>
          {lesson.lessonType === 'private' && (
            <span className="px-2 py-0.5 rounded bg-purple-100 dark:bg-purple-900/40">Private</span>
          )}
          {lesson.lessonType === 'camp' && (
            <span className="px-2 py-0.5 rounded bg-violet-100 dark:bg-violet-900/40">Camp</span>
          )}
        </div>

        {(termName || lesson.skillLevel || courtLabel) && (
          <dl className="grid gap-1 text-xs text-gray-600 dark:text-gray-400">
            {termName && (
              <div>
                <span className="font-medium text-gray-800 dark:text-gray-200">Term: </span>
                {termName}
              </div>
            )}
            {lesson.skillLevel && (
              <div>
                <span className="font-medium text-gray-800 dark:text-gray-200">Level: </span>
                {lesson.skillLevel}
              </div>
            )}
            {courtLabel && (
              <div className="flex items-start gap-1">
                <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden />
                <span>
                  <span className="font-medium text-gray-800 dark:text-gray-200">Courts: </span>
                  {courtLabel}
                </span>
              </div>
            )}
          </dl>
        )}

        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
            <Users className="h-4 w-4 shrink-0" aria-hidden />
            Students this session
            {!loadingEnrollments && rosterRows.length > 0 && (
              <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                ({rosterRows.length})
              </span>
            )}
          </h4>

          {!sessionScheduled && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Attendance can only be recorded for scheduled sessions.
            </p>
          )}

          {loadingEnrollments ? (
            <p className="text-gray-500 text-xs">Loading roster…</p>
          ) : rosterRows.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-xs">No enrollments for this session.</p>
          ) : (
            <>
              {sessionScheduled && (
                <div
                  className="hidden sm:grid pb-1.5 mb-1 border-b border-gray-200 dark:border-gray-700 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 grid-cols-[minmax(0,1fr)_6.5rem] items-end gap-x-3"
                  aria-hidden
                >
                  <span>Student</span>
                  <span className="text-center">Attendance</span>
                </div>
              )}
              <ul className="space-y-0 sm:space-y-2 pr-1 [scrollbar-width:thin]">
                {rosterRows.map((row) => {
                  const key = rosterRowKey(row);
                  const isMakeup =
                    row.enrollmentSource === 'makeup_session' || row.isMakeupBooking;
                  const scopeLabel =
                    !isMakeup && row.bookingScope === 'session'
                      ? getOccurrenceScopeLabel({
                          bookingScope: row.bookingScope,
                          lessonType: lesson.lessonType,
                        })
                      : null;
                  return (
                    <li
                      key={key}
                      className={`border-b border-gray-100 dark:border-gray-700 last:border-0 ${
                        sessionScheduled && row.bookingId
                          ? 'py-3 space-y-2 sm:py-0 sm:space-y-0 sm:pb-2 sm:grid sm:grid-cols-[minmax(0,1fr)_6.5rem] sm:items-center sm:gap-x-3'
                          : 'flex flex-wrap items-center gap-2 py-2 pb-2'
                      }`}
                    >
                      <div className="min-w-0 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span className="font-medium text-gray-900 dark:text-white">{row.studentName}</span>
                        {row.status === 'pending' && (
                          <span className="text-[10px] px-1.5 py-0 rounded bg-amber-100 dark:bg-amber-900/50 text-amber-900 dark:text-amber-100">
                            Unconfirmed
                          </span>
                        )}
                        {isMakeup && (
                          <span className="text-[10px] px-1.5 py-0 rounded bg-purple-100 dark:bg-purple-900/50 text-purple-900 dark:text-purple-100">
                            Make-up
                          </span>
                        )}
                        {scopeLabel && (
                          <span className="text-[10px] px-1.5 py-0 rounded bg-sky-100 dark:bg-sky-900/50 text-sky-900 dark:text-sky-100">
                            {scopeLabel}
                          </span>
                        )}
                        {row.isReportedAbsent && (
                          <span className="text-[10px] px-1.5 py-0 rounded bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-slate-100">
                            Reported absent
                          </span>
                        )}
                      </div>
                      {sessionScheduled && row.bookingId ? (
                        <div className="flex items-center gap-2 sm:justify-center">
                          <span className="w-14 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:hidden">
                            Attend.
                          </span>
                          <AttendanceToggle
                            current={row.attendanceStatus ?? 'scheduled'}
                            loading={markingBookingId === row.bookingId}
                            disabled={
                              markingBookingId != null && markingBookingId !== row.bookingId
                            }
                            onMark={(status) => void handleMarkAttendance(row, status)}
                          />
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        {allowSelfCheckIn && sessionScheduled && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4 shrink-0" aria-hidden />
              Your attendance
            </h4>
            {loadingSelfAttendance ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">Loading…</p>
            ) : (
              <CoachSelfCheckInActions
                rosterCount={rosterRows.length}
                markedCount={markedStudentCount}
                checkedIn={selfAttendance?.checkedIn ?? false}
                checkingIn={checkingInSelf}
                selfCheckInAvailable={selfAttendance?.selfCheckInAvailable ?? false}
                selfCheckInLocked={selfAttendance?.selfCheckInLocked ?? false}
                onCheckIn={() => handleSelfCheckIn('attended')}
                onUndo={() => handleSelfCheckIn('scheduled')}
              />
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
